import React, { useEffect, useMemo, useState } from 'react';
import {
  BankReference,
  CreditApplication,
  CreditApplicationFieldsValues,
  CreditApplicationType,
  getCreditApplicationTypeLabel,
  InvitedSignatures,
} from '@dill/dill-shared';
import { PreviewCreditApplicationData } from '../CreditApplicationComponents/PreviewCreditApplicationData';
import { useDecryption } from '../../../../utils/useDecryption';
import { decryptCreditApplicationData } from '../../../../utils/decryptCreditApplicationData';
import { DillButton } from '../../../general/Dill';
import { useAppDispatch, useAppSelector } from '../../../../redux/store';
import { getCreditApplicationTemplate } from '../../../../redux/services/creditApplicationsTemplatesService';

export interface ApplicationTile {
  id: string;
  supplierId: string;
  supplierName: string;
  templateId: string;
  status: 'DRAFT' | 'SENT' | 'SUBMITTED' | 'APPROVED' | 'REJECTED';
  completionPercentage: number;
  createdAt: string;
  updatedAt: string;
  applicationType: CreditApplicationType;
}

type EnrichedApplication = CreditApplication & { supplierName?: string | null };

interface CustomerApplicationDashboardProps {
  email: string;
  applications: EnrichedApplication[];
  onContinueApplication: (application: EnrichedApplication) => void;
  onEditApplication: (application: EnrichedApplication) => void | Promise<void>;
  onStartNewApplication: () => void;
  currentSupplierId: string;
  currentSupplierName: string;
  onBack?: () => void;
}

const STATUS_ORDER: string[] = [
  'SENT',
  'OPENED',
  'DRAFT',
  'AWAITING_SIGNATURE',
  'SUBMITTED',
  'INTERNAL_SIGNATURE',
  'READY_FOR_DECISION',
  'INTERNALLY_APPROVED',
  'INTERNALLY_APPROVED_COD',
  'APPROVED',
  'REJECTED',
];

const STATUS_CONFIG: Record<string, { label: string; pill: string; bar: string; icon: string }> = {
  SENT: {
    label: 'Sent',
    pill: 'GREY_100-BG GREY_500-CLR',
    bar: 'GREY_400-BG',
    icon: '📤',
  },
  OPENED: {
    label: 'Opened',
    pill: 'GREY_100-BG GREY_500-CLR',
    bar: 'GREY_400-BG',
    icon: '👁️',
  },
  DRAFT: {
    label: 'Draft',
    pill: 'GREY_100-BG GREY_500-CLR',
    bar: 'GREY_400-BG',
    icon: '✏️',
  },
  AWAITING_SIGNATURE: {
    label: 'Awaiting Signature',
    pill: 'GREY_100-BG GREY_500-CLR',
    bar: 'GREY_400-BG',
    icon: '✍️',
  },
  SUBMITTED: {
    label: 'Submitted',
    pill: 'WARNING_100-BG WARNING_500-CLR',
    bar: 'WARNING_500-BG',
    icon: '📋',
  },
  INTERNAL_SIGNATURE: {
    label: 'Under Internal Review',
    pill: 'WARNING_100-BG WARNING_500-CLR',
    bar: 'WARNING_500-BG',
    icon: '🔏',
  },
  READY_FOR_DECISION: {
    label: 'Ready for Decision',
    pill: 'INFO_100-BG INFO_500-CLR',
    bar: 'INFO_500-BG',
    icon: '⚖️',
  },
  INTERNALLY_APPROVED: {
    label: 'Internally Approved',
    pill: 'SUCCESS_100-BG SUCCESS_500-CLR',
    bar: 'SUCCESS_500-BG',
    icon: '✅',
  },
  INTERNALLY_APPROVED_COD: {
    label: 'Internally Approved',
    pill: 'SUCCESS_100-BG SUCCESS_500-CLR',
    bar: 'SUCCESS_500-BG',
    icon: '✅',
  },
  APPROVED: {
    label: 'Approved',
    pill: 'SUCCESS_100-BG SUCCESS_500-CLR',
    bar: 'SUCCESS_500-BG',
    icon: '🎉',
  },
  REJECTED: {
    label: 'Rejected',
    pill: 'ERROR_100-BG ERROR_500-CLR',
    bar: 'ERROR_500-BG',
    icon: '❌',
  },
};

const getStatusConfig = (status: string) =>
  STATUS_CONFIG[status] ?? {
    label: status,
    pill: 'GREY_100-BG GREY_500-CLR',
    bar: 'GREY_400-BG',
    icon: '📄',
  };

const calculateCompletion = (app: CreditApplication): number => {
  if (!app.fieldValues || app.fieldValues.length === 0) return 0;
  const filled = app.fieldValues.filter((f) => f.value && f.value !== '').length;
  return Math.round((filled / app.fieldValues.length) * 100);
};

const formatDate = (date: Date | string | null | undefined): string => {
  if (!date) return '';
  return new Date(date).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

const sortByStatus = (apps: EnrichedApplication[]) => {
  const byDate = [...apps].sort(
    (a, b) => new Date(b.updatedAt ?? 0).getTime() - new Date(a.updatedAt ?? 0).getTime()
  );
  return byDate.sort((a, b) => {
    const ai = STATUS_ORDER.indexOf(a.status);
    const bi = STATUS_ORDER.indexOf(b.status);
    return (ai === -1 ? 999 : ai) - (bi === -1 ? 999 : bi);
  });
};

const isActionable = (status: string) => ['SENT', 'OPENED', 'DRAFT'].includes(status);
const isEditable = (status: string) =>
  ['AWAITING_SIGNATURE', 'SUBMITTED', 'INTERNAL_SIGNATURE', 'READY_FOR_DECISION'].includes(status);
const isTerminal = (status: string) =>
  ['INTERNALLY_APPROVED', 'INTERNALLY_APPROVED_COD', 'APPROVED', 'REJECTED'].includes(status);

const AppCard: React.FC<{
  app: EnrichedApplication;
  onAction: () => void;
  onView: () => void;
  onEdit: () => void;
  showSupplierName?: boolean;
}> = ({ app, onAction, onView, onEdit, showSupplierName }) => {
  const cfg = getStatusConfig(app.status);
  const completion = app.status === 'DRAFT' ? calculateCompletion(app) : null;
  const actionable = isActionable(app.status);
  const editable = isEditable(app.status);
  const terminal = isTerminal(app.status);
  const dateLabel = formatDate(app.updatedAt ?? app.createdAt);
  const appLabel = getCreditApplicationTypeLabel(app.applicationType, { long: true });
  const companyName = app.fieldValues?.find((f) => f.id === 'customer-or-company-name')?.value;
  const amountRaw = app.fieldValues?.find((f) => f.id.includes('amount-of'))?.value;
  const amountLabel = amountRaw
    ? `$${Number(amountRaw).toLocaleString('en-US', { maximumFractionDigits: 0 })}`
    : null;

  return (
    <div
      className={`group flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4 bg-white border border-gray-100 rounded-xl px-4 py-3.5 transition-all duration-150 ${
        terminal ? 'cursor-default' : 'hover:border-blue-200 hover:shadow-sm cursor-pointer'
      }`}>
      <div className="flex items-stretch gap-4 flex-1 min-w-0">
        {/* Left accent */}
        <div className={`w-1 self-stretch rounded-full flex-shrink-0 ${cfg.bar}`} />

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <span className="text-sm font-semibold text-gray-900">{appLabel}</span>
            <span
              className={`text-xs font-medium px-2 py-0.5 rounded-full whitespace-nowrap ${cfg.pill}`}>
              {cfg.label}
            </span>
          </div>

          <div className="flex items-center gap-3 flex-wrap">
            {showSupplierName && app.supplierName && (
              <span className="text-xs text-gray-500">{app.supplierName}</span>
            )}
            {companyName && (
              <span className="text-xs text-gray-600 font-medium">{companyName}</span>
            )}
            {amountLabel && <span className="text-xs text-gray-500">Requested {amountLabel}</span>}
            {dateLabel && <span className="text-xs text-gray-400">Updated {dateLabel}</span>}
          </div>

          {completion !== null && (
            <div className="mt-2 flex items-center gap-2">
              <div className="flex-1 max-w-[120px] bg-gray-100 rounded-full h-1.5 overflow-hidden">
                <div
                  className={`h-1.5 rounded-full transition-all ${cfg.bar}`}
                  style={{ width: `${completion}%` }}
                />
              </div>
              <span className="text-xs text-gray-500 font-medium">{completion}% complete</span>
            </div>
          )}
        </div>
      </div>

      {/* Actions */}
      <div className="flex items-center gap-2 flex-shrink-0 justify-end">
        {editable && (
          <DillButton
            onClick={(e) => {
              e.stopPropagation();
              onEdit();
            }}
            label="Edit"
            variant="secondary"
          />
        )}
        <DillButton
          onClick={(e) => {
            e.stopPropagation();
            onView();
          }}
          label="View"
          variant="secondary"
        />
        {actionable && !(showSupplierName && app.status === 'DRAFT') && (
          <DillButton
            onClick={(e) => {
              e.stopPropagation();
              onAction();
            }}
            label={'Continue →'}
          />
        )}
      </div>
    </div>
  );
};

export const CustomerApplicationDashboard: React.FC<CustomerApplicationDashboardProps> = ({
  email,
  applications,
  onContinueApplication,
  onEditApplication,
  onStartNewApplication,
  currentSupplierId,
  currentSupplierName,
  onBack,
}) => {
  const [viewingApp, setViewingApp] = useState<EnrichedApplication | null>(null);
  const [decryptedFieldValues, setDecryptedFieldValues] = useState<CreditApplicationFieldsValues[]>(
    []
  );
  const [decryptedBankReferences, setDecryptedBankReferences] = useState<BankReference[]>([]);
  const [decryptedInvitedSignatures, setDecryptedInvitedSignatures] = useState<InvitedSignatures[]>(
    []
  );
  const [isDecrypting, setIsDecrypting] = useState(false);
  const { decryptValue } = useDecryption();
  const dispatch = useAppDispatch();
  const { creditApplicationTemplate } = useAppSelector((state) => state.creditApplicationTemplates);

  useEffect(() => {
    if (!viewingApp) {
      setDecryptedFieldValues([]);
      setDecryptedBankReferences([]);
      setDecryptedInvitedSignatures([]);
      return;
    }

    const decrypt = async () => {
      setIsDecrypting(true);
      try {
        const {
          decryptedFields,
          decryptedBankReferences: bankRefs,
          decryptedInvitedSignatures: signatures,
        } = await decryptCreditApplicationData(viewingApp, decryptValue);
        console.log('Decrypted data:', { viewingApp, decryptedFields, bankRefs, signatures });
        setDecryptedFieldValues(decryptedFields);
        setDecryptedBankReferences(bankRefs);
        setDecryptedInvitedSignatures(signatures);
      } catch (error) {
        console.error('Error decrypting fields:', error);
      } finally {
        setIsDecrypting(false);
      }
    };

    decrypt();
  }, [viewingApp, decryptValue]);

  useEffect(() => {
    if (viewingApp?.templateId) {
      dispatch(getCreditApplicationTemplate({ templateId: viewingApp.templateId }));
    }
  }, [viewingApp?.templateId]);

  const documentTextById = useMemo(() => {
    const map = new Map<string, string>();
    if (creditApplicationTemplate?.id !== viewingApp?.templateId) return map;
    creditApplicationTemplate?.steps?.forEach((step) => {
      step.fields?.forEach((field) => {
        field.unGroupedFields?.forEach((f) => {
          if (f.fromField === 'DOCUMENT_FIELD' && f.documentText) map.set(f.id, f.documentText);
        });
        field.groupedFields?.forEach((group) => {
          group.fields?.forEach((f) => {
            if (f.fromField === 'DOCUMENT_FIELD' && f.documentText) map.set(f.id, f.documentText);
          });
        });
      });
    });
    return map;
  }, [creditApplicationTemplate, viewingApp?.templateId]);

  const previewFieldValues = useMemo(() => {
    if (documentTextById.size === 0) return decryptedFieldValues;
    return decryptedFieldValues.map((fv) =>
      fv.fromField === 'DOCUMENT_FIELD' && !fv.value && documentTextById.has(fv.id)
        ? { ...fv, value: documentTextById.get(fv.id) }
        : fv
    );
  }, [decryptedFieldValues, documentTextById]);

  const { currentSupplierApps, otherSupplierApps } = useMemo(() => {
    const current: EnrichedApplication[] = [];
    const other: EnrichedApplication[] = [];
    applications.forEach((app) => {
      if (app.supplierId === currentSupplierId) {
        current.push(app);
      } else {
        other.push(app);
      }
    });
    return {
      currentSupplierApps: sortByStatus(current),
      otherSupplierApps: sortByStatus(other),
    };
  }, [applications, currentSupplierId]);

  return (
    <div className="min-h-screen flex justify-center p-4 w-full md:w-5/6 lg:w-2/3">
      <div className="w-full max-w-2/3">
        {/* Back button */}
        {onBack && (
          <div className="mb-4">
            <button
              onClick={onBack}
              className="flex items-center gap-2 text-gray-600 hover:text-gray-800 transition-colors text-sm font-medium">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 19l-7-7 7-7"
                />
              </svg>
              Back
            </button>
          </div>
        )}

        {/* Welcome card */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden mb-4">
          <div className="bg-gradient-to-r from-blue-600 to-blue-500 px-6 py-5">
            <p className="text-blue-100 text-xs mb-0.5">Welcome back</p>
            <p className="text-white font-semibold text-sm truncate">{email}</p>
          </div>
          <div className="px-6 py-4 flex items-center justify-between">
            <div>
              <p className="text-xs text-gray-400 uppercase tracking-wide font-medium mb-0.5">
                Applying to
              </p>
              <p className="text-base font-bold text-gray-900">{currentSupplierName}</p>
            </div>
            <DillButton onClick={onStartNewApplication} label="+ New Application" />
          </div>
        </div>

        {/* Current supplier applications */}
        <div className="mb-4">
          {currentSupplierApps.length === 0 ? (
            <div className="bg-white rounded-2xl border border-dashed border-gray-200 p-8 text-center">
              <div className="w-12 h-12 bg-blue-50 rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="text-xl">📄</span>
              </div>
              <p className="text-sm font-medium text-gray-700 mb-1">No applications yet</p>
              <p className="text-xs text-gray-400 mb-4">
                Start your first application with {currentSupplierName}
              </p>
              <DillButton onClick={onStartNewApplication} label="Start Application" />
            </div>
          ) : (
            <div className="space-y-2">
              {currentSupplierApps.map((app) => (
                <AppCard
                  key={app.id}
                  app={app}
                  onAction={() => onContinueApplication(app)}
                  onView={() => setViewingApp(app)}
                  onEdit={() => onEditApplication(app)}
                />
              ))}
            </div>
          )}
        </div>

        {/* Other suppliers */}
        {otherSupplierApps.length > 0 && (
          <div>
            <div className="flex items-center gap-2 mb-3 px-1">
              <div className="flex-1 h-px bg-gray-200" />
              <span className="text-xs text-gray-400 font-medium uppercase tracking-wide whitespace-nowrap">
                Other suppliers
              </span>
              <div className="flex-1 h-px bg-gray-200" />
            </div>
            <div className="space-y-2">
              {otherSupplierApps.map((app) => (
                <AppCard
                  key={app.id}
                  app={app}
                  onAction={() => onContinueApplication(app)}
                  onView={() => setViewingApp(app)}
                  onEdit={() => onEditApplication(app)}
                  showSupplierName
                />
              ))}
            </div>
          </div>
        )}
      </div>

      {/* View application modal */}
      {viewingApp && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4"
          onClick={() => setViewingApp(null)}>
          <div
            className="bg-white rounded-2xl shadow-xl w-full max-w-3xl max-h-[90vh] flex flex-col overflow-hidden"
            onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <p className="text-sm font-semibold text-gray-900">Application Details</p>
              <button
                onClick={() => setViewingApp(null)}
                className="text-gray-400 hover:text-gray-600 transition-colors text-lg leading-none">
                ✕
              </button>
            </div>
            <div className="overflow-y-auto flex-1 px-6 py-4">
              {isDecrypting ? (
                <div className="flex items-center justify-center py-12">
                  <div className="w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
                </div>
              ) : (
                <PreviewCreditApplicationData
                  formData={previewFieldValues}
                  bankReference={decryptedBankReferences}
                  inviteSignatures={decryptedInvitedSignatures}
                  isOnlyView={true}
                />
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
